import React from 'react';
import { Link } from 'react-router-dom';
import { CategoryIcon, ChevronIcon } from './icons';
import LiveSearchBox from './LiveSearchBox';
import { HelpCategory, HelpArticleMeta } from './helpdeskApi';

interface HubViewProps {
    categories: HelpCategory[];
    popular: HelpArticleMeta[];
}

/**
 * Help Center home: search hero, popular-articles strip, and the 9 category
 * cards. Ported from `hubPage()` in prototype/lib/templates.js. Degrades to
 * a friendly empty state per section when categories/articles are not yet
 * published (production publish-gate returns [] until content is approved).
 */
const HubView: React.FC<HubViewProps> = ({ categories, popular }) => {
    const hasCategories = categories.length > 0;
    const hasPopular = popular.length > 0;

    return (
        <>
            <section className="hc-hero">
                <div className="hc-wrap hc-hero__inner">
                    <p className="hc-hero__eyebrow">How can we help?</p>
                    <h1 className="hc-hero__title">TrafficCreator Help Center</h1>
                    <p className="hc-hero__sub">
                        Guides and answers for campaigns, GA4 analytics, billing, and your account.
                    </p>
                    <LiveSearchBox variant="hero" />
                    <p className="hc-hero__hints">
                        Popular:{' '}
                        <Link to="/helpdesk/search?q=traffic+not+showing">traffic not showing</Link> ·{' '}
                        <Link to="/helpdesk/search?q=payment+failed">payment failed</Link> ·{' '}
                        <Link to="/helpdesk/search?q=measurement+id">measurement ID</Link> ·{' '}
                        <Link to="/helpdesk/search?q=pause+campaign">pause a campaign</Link>
                    </p>
                </div>
            </section>

            <section className="hc-wrap hc-section">
                <div className="hc-section__head">
                    <h2>Popular articles</h2>
                    <p>The answers customers open most often.</p>
                </div>
                {hasPopular ? (
                    <div className="hc-pop-grid">
                        {popular.map((article, index) => (
                            <Link key={article.url} className="hc-pop-card" to={article.url}>
                                <span className="hc-pop-card__rank">{index + 1}</span>
                                <span className="hc-pop-card__title">{article.title}</span>
                                <span className="hc-pop-card__cat">{article.categoryTitle}</span>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <p className="hc-ink-3-note">Popular articles are coming soon.</p>
                )}
            </section>

            <section className="hc-wrap hc-section">
                <div className="hc-section__head">
                    <h2>Browse by category</h2>
                    <p>Every guide, grouped by what you are trying to do.</p>
                </div>
                {hasCategories ? (
                    <div className="hc-cat-grid">
                        {categories.map((cat) => (
                            <Link key={cat.slug} className="hc-cat-card" to={`/helpdesk/${cat.slug}`}>
                                <span className="hc-cat-card__icon">
                                    <CategoryIcon name={cat.icon} size={22} />
                                </span>
                                <span className="hc-cat-card__body">
                                    <span className="hc-cat-card__title">{cat.title}</span>
                                    <span className="hc-cat-card__desc">{cat.short}</span>
                                </span>
                                <span className="hc-cat-card__meta">
                                    {cat.articleCount} article{cat.articleCount === 1 ? '' : 's'}
                                    <ChevronIcon size={16} />
                                </span>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="hc-noresult">
                        <h2>The help center is being finalized.</h2>
                        <p>Categories and articles will appear here as soon as they are published.</p>
                    </div>
                )}
            </section>

            <section className="hc-wrap hc-section">
                <div className="hc-support-band">
                    <div className="hc-support-band__text">
                        <h2>Still stuck?</h2>
                        <p>
                            If self-service did not solve it, open a ticket. Include your <strong>project ID</strong> and a
                            screenshot for the fastest campaign help.
                        </p>
                    </div>
                    <div className="hc-support-band__actions">
                        <Link className="hc-btn hc-btn--primary hc-btn--lg" to="/helpdesk/support/contact-trafficcreator-support">
                            Contact support
                        </Link>
                        <Link className="hc-btn hc-btn--ghost hc-btn--lg" to="/helpdesk/support/submit-ticket-with-right-details">
                            How to write a good ticket
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
};

export default HubView;
